/**
 * Imagem de uma mensagem do WhatsApp na Central (04/11/2026).
 *
 * - No balão: a miniatura (URL assinada de 15 min). Se a URL venceu com a
 *   conversa aberta, pede outra uma vez (`GET mensagens/{id}/imagem`).
 * - Ao clicar: a imagem grande no mesmo `ImagemViewer` das Solicitações
 *   (zoom, imprimir, baixar), só leitura. Ela vem pela API autenticada: o
 *   bucket não tem CORS e o visualizador precisa ler o arquivo.
 */
import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";

import ImagemViewer from "../../components/ImagemViewer";
import { MiniSpinner } from "../../components/LoadingMarcenaria";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { arquivoDaImagem, renovarMiniatura } from "../../services/centralProjetos";
import { buscarArquivoComAuth } from "../../utils/documentos";
import { autorDaMensagem, horaCurta } from "./apresentacao";

/** Largura máxima da miniatura no balão (o resto do balão acompanha). */
const LARGURA_MAXIMA = 260;
const ALTURA_MAXIMA = 340;
const PROPORCAO_PADRAO = 4 / 3;

/*
 * A altura sai da largura (padding em %), reservada antes de a imagem
 * carregar: `aspect-ratio` num <button> crescia depois e a conversa deixava
 * de abrir no fim. Foto muito alta é cortada (object-fit) na altura máxima.
 */
const Miniatura = styled.button`
  position: relative;
  display: block;
  width: min(${LARGURA_MAXIMA}px, 100%);
  height: 0;
  padding: 0 0 ${({ $proporcao }) => (100 / $proporcao).toFixed(2)}%;
  margin: 0 0 4px;
  overflow: hidden;
  border: 0;
  border-radius: 7px;
  background: var(--cp-midia, rgba(11, 15, 26, 0.05));
  cursor: zoom-in;

  img {
    position: absolute;
    inset: 0;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

const Carregando = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: grid;
  place-items: center;
  background: rgba(10, 12, 18, 0.6);
`;

function blobParaDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const leitor = new FileReader();
    leitor.onload = () => resolve(leitor.result);
    leitor.onerror = () => reject(leitor.error || new Error("Não foi possível ler a imagem."));
    leitor.readAsDataURL(blob);
  });
}

/** A imagem grande, lida pela API, no visualizador. */
function VisualizadorDaImagem({ mensagem, onFechar }) {
  const { request } = useAuth();
  const toast = useToast();
  const [imagem, setImagem] = useState(null);
  const renovarSessao = useCallback(() => request("/api/me", { silentError: true }), [request]);

  useEffect(() => {
    const controle = new AbortController();
    buscarArquivoComAuth(arquivoDaImagem(mensagem.id), { signal: controle.signal, renovarSessao })
      .then(blobParaDataUrl)
      .then(setImagem)
      .catch((erro) => {
        if (erro?.name === "AbortError") return;
        toast.error(erro?.message || "Não foi possível abrir a imagem.");
        onFechar();
      });
    return () => controle.abort();
  }, [mensagem.id, onFechar, renovarSessao, toast]);

  if (!imagem) {
    return (
      <Carregando onMouseDown={(e) => { if (e.target === e.currentTarget) onFechar(); }}>
        <MiniSpinner size={22} label="Abrindo imagem" />
      </Carregando>
    );
  }
  const legenda = String(mensagem.texto || "").trim();
  return (
    <ImagemViewer
      foto={{ id: mensagem.id, imagem, legenda: legenda || "Imagem", editada: false }}
      cabecalho={{ titulo: autorDaMensagem(mensagem), detalhe: horaCurta(mensagem.enviada_em) }}
      onFechar={onFechar}
    />
  );
}

/** Miniatura da imagem no balão; `null` quando a imagem não foi guardada. */
export default function ImagemDaMensagem({ mensagem }) {
  const { request } = useAuth();
  const [miniatura, setMiniatura] = useState(mensagem.imagem?.miniatura || "");
  const [renovou, setRenovou] = useState(false);
  const [aberta, setAberta] = useState(false);
  const fechar = useCallback(() => setAberta(false), []);

  useEffect(() => {
    setMiniatura(mensagem.imagem?.miniatura || "");
    setRenovou(false);
  }, [mensagem.imagem?.miniatura]);

  if (!mensagem.imagem) return null;

  async function aoFalhar() {
    // URL de 15 min vencida com a conversa aberta: uma renovação só.
    if (renovou) return;
    setRenovou(true);
    try {
      const nova = await renovarMiniatura(request, mensagem.id);
      if (nova?.miniatura) setMiniatura(nova.miniatura);
    } catch {
      // Sem a URL nova a miniatura fica vazia; clicar ainda abre pela API.
    }
  }

  const { largura, altura } = mensagem.imagem;
  const proporcao = Math.max(largura && altura ? largura / altura : PROPORCAO_PADRAO, LARGURA_MAXIMA / ALTURA_MAXIMA);
  return (
    <>
      <Miniatura
        type="button"
        $proporcao={proporcao}
        onClick={() => setAberta(true)}
        aria-label={`Abrir a imagem de ${autorDaMensagem(mensagem)}`}
        title="Abrir a imagem"
      >
        {miniatura ? <img src={miniatura} alt="" loading="lazy" onError={aoFalhar} /> : null}
      </Miniatura>
      {aberta ? <VisualizadorDaImagem mensagem={mensagem} onFechar={fechar} /> : null}
    </>
  );
}
